import { useState, useRef } from 'react';
import './App.css';

// 이미지를 base64로 변환 + 리사이즈 (API 비용 절감)
function resizeAndConvertToBase64(file, maxWidth = 800) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = reject;
    img.src = url;
  });
}

const COLOR_MAP = {
  '봄 웜톤': { bg: '#fff5e6', accent: '#f4a030', emoji: '🌸' },
  '여름 쿨톤': { bg: '#e8f4fd', accent: '#5ba3d9', emoji: '🩵' },
  '가을 웜톤': { bg: '#fdf0e6', accent: '#c8763a', emoji: '🍂' },
  '겨울 쿨톤': { bg: '#eef0f8', accent: '#5560a4', emoji: '❄️' },
};

function getColorTheme(type) {
  for (const key of Object.keys(COLOR_MAP)) {
    if (type?.includes(key.split(' ')[0]) && type?.includes(key.split(' ')[1])) {
      return COLOR_MAP[key];
    }
  }
  return { bg: '#f9f5ff', accent: '#b47ee8', emoji: '✨' };
}

// ── 결과 화면 ──
function ResultPage({ result, photoPreview, height, weight, onReset }) {
  const { personalColor, bodyType, styleRecommendation, celebrities } = result;
  const theme = getColorTheme(personalColor?.type);

  return (
    <div className="page">
      <div className="result-wrap">

        {/* 헤더 */}
        <div className="result-header" style={{ background: theme.bg }}>
          <img src={photoPreview} alt="프로필" className="result-avatar" />
          <div className="result-header-info">
            <p className="result-meta">{height}cm · {weight}kg</p>
            <h2 className="result-name">나의 스타일 리포트</h2>
          </div>
        </div>

        {/* 퍼스널 컬러 */}
        <section className="report-section">
          <div className="section-title-row">
            <span className="section-emoji">{theme.emoji}</span>
            <h3>퍼스널 컬러</h3>
            <span className="badge" style={{ background: theme.accent }}>{personalColor?.type}</span>
          </div>
          <p className="section-desc">{personalColor?.description}</p>
          <div className="color-group">
            <p className="color-group-label">추천 컬러</p>
            <div className="tag-list">
              {personalColor?.bestColors?.map((c, i) => (
                <span key={i} className="tag tag-good">{c}</span>
              ))}
            </div>
          </div>
          <div className="color-group">
            <p className="color-group-label">피해야 할 컬러</p>
            <div className="tag-list">
              {personalColor?.avoidColors?.map((c, i) => (
                <span key={i} className="tag tag-bad">{c}</span>
              ))}
            </div>
          </div>
        </section>

        {/* 체형 */}
        <section className="report-section">
          <div className="section-title-row">
            <span className="section-emoji">👗</span>
            <h3>체형 분석</h3>
            <span className="badge badge-gray">{bodyType?.type}</span>
          </div>
          <p className="section-desc">{bodyType?.description}</p>
        </section>

        {/* 스타일 추천 */}
        <section className="report-section">
          <div className="section-title-row">
            <span className="section-emoji">✦</span>
            <h3>스타일 추천</h3>
          </div>
          <p className="section-desc">{styleRecommendation?.overall}</p>

          <div className="style-detail">
            <div className="style-item">
              <span className="style-item-label">상의</span>
              <span className="style-item-text">{styleRecommendation?.tops}</span>
            </div>
            <div className="style-item">
              <span className="style-item-label">하의</span>
              <span className="style-item-text">{styleRecommendation?.bottoms}</span>
            </div>
          </div>

          <div className="color-group">
            <p className="color-group-label">추천 스타일</p>
            <div className="tag-list">
              {styleRecommendation?.recommended?.map((s, i) => (
                <span key={i} className="tag tag-style">{s}</span>
              ))}
            </div>
          </div>
          <div className="color-group">
            <p className="color-group-label">피해야 할 스타일</p>
            <div className="tag-list">
              {styleRecommendation?.avoid?.map((s, i) => (
                <span key={i} className="tag tag-bad">{s}</span>
              ))}
            </div>
          </div>
        </section>

        {/* 참고 스타일 */}
        {celebrities?.length > 0 && (
          <section className="report-section">
            <div className="section-title-row">
              <span className="section-emoji">⭐</span>
              <h3>참고 스타일</h3>
            </div>
            <div className="tag-list">
              {celebrities.map((c, i) => (
                <span key={i} className="tag tag-celeb">{c}</span>
              ))}
            </div>
          </section>
        )}

        <button className="btn-secondary full-width" onClick={onReset}>
          다시 분석하기
        </button>
      </div>
    </div>
  );
}

// ── 로딩 화면 ──
function LoadingPage() {
  return (
    <div className="page">
      <div className="card center-card">
        <div className="loading-spinner" />
        <h2 className="loading-title">분석 중이에요</h2>
        <p className="loading-sub">사진과 체형 정보를 바탕으로<br />퍼스널 컬러와 스타일을 분석하고 있습니다.</p>
      </div>
    </div>
  );
}

// ── 메인 입력 화면 ──
function InputPage({ onSubmit }) {
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const fileInputRef = useRef(null);

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!photo || !height || !weight) return;
    const base64 = await resizeAndConvertToBase64(photo);
    onSubmit({ photo: base64, photoPreview, height, weight });
  };

  const isReady = photo && height && weight;

  return (
    <div className="page">
      <div className="card">
        <div className="header">
          <div className="logo-badge">✦</div>
          <h1>Personal Stylist Studio</h1>
          <p className="subtitle">사진과 체형 정보를 입력하면<br />나만의 퍼스널 컬러와 스타일을 알려드려요</p>
        </div>

        <form onSubmit={handleSubmit} className="form">
          <div className="section">
            <label className="section-label">내 사진 업로드</label>
            <div
              className={`drop-zone ${photoPreview ? 'has-photo' : ''}`}
              onClick={() => fileInputRef.current.click()}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {photoPreview ? (
                <div className="preview-wrapper">
                  <img src={photoPreview} alt="미리보기" className="preview-img" />
                  <div className="preview-overlay"><span>사진 변경</span></div>
                </div>
              ) : (
                <div className="drop-content">
                  <div className="drop-icon">📷</div>
                  <p className="drop-text">클릭하거나 사진을 끌어다 놓으세요</p>
                  <p className="drop-hint">JPG, PNG, WEBP 지원</p>
                </div>
              )}
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
          </div>

          <div className="section">
            <label className="section-label">체형 정보</label>
            <div className="input-row">
              <div className="input-group">
                <label className="input-label">키</label>
                <div className="input-with-unit">
                  <input type="number" className="input-field" placeholder="예) 165" value={height}
                    onChange={(e) => setHeight(e.target.value)} min="100" max="250" />
                  <span className="unit">cm</span>
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">몸무게</label>
                <div className="input-with-unit">
                  <input type="number" className="input-field" placeholder="예) 55" value={weight}
                    onChange={(e) => setWeight(e.target.value)} min="20" max="300" />
                  <span className="unit">kg</span>
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className={`btn-primary ${!isReady ? 'disabled' : ''}`} disabled={!isReady}>
            퍼스널 컬러 &amp; 스타일 분석하기
          </button>
        </form>
      </div>
    </div>
  );
}

// ── App 루트 ──
export default function App() {
  const [step, setStep] = useState('input'); // 'input' | 'loading' | 'result' | 'error'
  const [inputData, setInputData] = useState(null);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async ({ photo, photoPreview, height, weight }) => {
    setInputData({ photoPreview, height, weight });
    setStep('loading');

    try {
      const res = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo, height, weight }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || '분석에 실패했습니다.');
      setResult(data.result);
      setStep('result');
    } catch (err) {
      setErrorMsg(err.message);
      setStep('error');
    }
  };

  const handleReset = () => {
    setStep('input');
    setResult(null);
    setInputData(null);
    setErrorMsg('');
  };

  if (step === 'loading') return <LoadingPage />;

  if (step === 'result') return (
    <ResultPage
      result={result}
      photoPreview={inputData.photoPreview}
      height={inputData.height}
      weight={inputData.weight}
      onReset={handleReset}
    />
  );

  if (step === 'error') return (
    <div className="page">
      <div className="card center-card">
        <div className="error-icon">⚠️</div>
        <h2>오류가 발생했어요</h2>
        <p className="loading-sub">{errorMsg}</p>
        <button className="btn-secondary" onClick={handleReset} style={{ marginTop: 16 }}>다시 시도</button>
      </div>
    </div>
  );

  return <InputPage onSubmit={handleSubmit} />;
}
